import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { 
  Eye, 
  EyeOff, 
  CheckCircle, 
  XCircle,
  User,
  Lock,
  Mail,
  ArrowLeft
} from 'lucide-react';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetStep, setResetStep] = useState('email'); // 'email', 'code', 'newPassword'
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { login, forgotPassword, verifyResetCode, resetPassword } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  // Test login API directly
  const testLoginAPI = async () => {
    try {
      console.log('🧪 Testing login API directly...');
      
      // Get current form values
      const currentUsername = document.querySelector('input[name="username"]')?.value;
      const currentPassword = document.querySelector('input[name="password"]')?.value;
      
      if (!currentUsername || !currentPassword) {
        toast.error('Please enter username and password first');
        return;
      }
      
      const response = await fetch('https://artmltapi.kssvnr.com/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: currentUsername,
          password: currentPassword
        })
      });
      
      const data = await response.json();
      console.log('🧪 Direct API response:', data);
      
      if (data.success && data.data && data.data.token) {
        toast.success('API test successful! Check console for details.');
      } else {
        toast.error('API test failed - invalid response format');
      }
    } catch (error) {
      console.error('🧪 API test error:', error);
      toast.error('API test failed - connection error');
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Show loading message
      const loadingToast = toast.loading('Authenticating...');
      
      // Attempt login through API
      const response = await login(data.username, data.password);
      console.log('Login component received response:', response);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Handle API response format
      let user;
      if (response && response.success && response.data && response.data.user) {
        // API response format: { success: true, data: { user: {...} } }
        user = response.data.user;
        console.log('Extracted user from API response:', user);
        toast.success(`Welcome back, ${user.name || user.username}!`);
      } else if (response && response.user) {
        // Direct user object format (fallback)
        user = response.user;
        console.log('Extracted user from direct response:', user);
        toast.success(`Welcome back, ${user.name || user.username}!`);
      } else if (response && response.username) {
        // Username-only format (demo mode)
        user = response;
        console.log('Using demo mode user:', user);
        toast.success(`Welcome back, ${user.name || user.username}! (Demo Mode)`);
      } else {
        console.error('Invalid response format:', response);
        throw new Error('Invalid response format from login');
      }
      
      // Navigate to appropriate dashboard based on user role
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific error cases
      let errorMessage = 'Login failed';
      
      if (error.message.includes('Invalid credentials')) {
        errorMessage = 'Invalid username or password';
      } else if (error.message.includes('API login failed')) {
        errorMessage = 'Using demo mode (API unavailable)';
        toast.info('Backend API is not available. Using demo authentication.', { duration: 4000 });
      } else if (error.message.includes('Network Error') || error.message.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection.';
      } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorMessage = 'Invalid credentials';
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        errorMessage = 'Access denied. Please contact administrator.';
      } else if (error.message.includes('404') || error.message.includes('Not Found')) {
        errorMessage = 'Authentication service not found. Please contact administrator.';
      } else if (error.message.includes('Invalid response format')) {
        errorMessage = 'Unexpected response format from server';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (!forgotPasswordEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsLoading(true);
    try {
      // Show loading message
      const loadingToast = toast.loading('Sending reset code...');
      
      // Attempt to send reset code through API
      const result = await forgotPassword(forgotPasswordEmail);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Show success message
      toast.success(result.message);
      
      // In demo mode, show the code. In production, this would be hidden
      if (result.code) {
        toast.success(`Demo: Your reset code is ${result.code}`, { duration: 5000 });
      }
      
      setResetStep('code');
    } catch (error) {
      console.error('Forgot password error:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to send reset code';
      
      if (error.message.includes('API forgot password failed')) {
        errorMessage = 'Using demo mode (API unavailable)';
        toast.info('Backend API is not available. Using demo password reset.', { duration: 4000 });
      } else if (error.message.includes('Network Error') || error.message.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection.';
      } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message.includes('404') || error.message.includes('Not Found')) {
        errorMessage = 'Email not found in our system.';
      } else if (error.message.includes('429') || error.message.includes('Too Many Requests')) {
        errorMessage = 'Too many requests. Please wait before trying again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!resetCode) {
      toast.error('Please enter the reset code');
      return;
    }
    
    if (resetCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setIsLoading(true);
    try {
      await verifyResetCode(forgotPasswordEmail, resetCode);
      toast.success('Code verified successfully');
      setResetStep('newPassword');
    } catch (error) {
      toast.error(error.message || 'Invalid reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    try {
      // Show loading message
      const loadingToast = toast.loading('Resetting password...');
      
      // Attempt to reset password through API
      const result = await resetPassword(forgotPasswordEmail, resetCode, newPassword);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Show success message
      toast.success(result.message || 'Password reset successfully! Please login with your new password.');
      
      // Reset all states and go back to login
      setShowForgotPassword(false);
      setResetStep('email');
      setForgotPasswordEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Reset password error:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to reset password';
      
      if (error.message.includes('API reset password failed')) {
        errorMessage = 'Using demo mode (API unavailable)';
        toast.info('Backend API is not available. Using demo password reset.', { duration: 4000 });
      } else if (error.message.includes('Invalid reset code')) {
        errorMessage = 'Invalid or expired reset code. Please request a new one.';
      } else if (error.message.includes('Network Error') || error.message.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection.';
      } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message.includes('400') || error.message.includes('Bad Request')) {
        errorMessage = 'Invalid request. Please check your input.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForgotPasswordFlow = () => {
    setShowForgotPassword(false);
    setResetStep('email');
    setForgotPasswordEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: '#2c6624' }}>
      <div className="mb-6">
        <h2 className="text-center text-yellow-300 text-xl sm:text-2xl font-semibold">
          Tamilnadu Agricultural Research Trial Management
        </h2>
          </div>
      {/* 3-column responsive layout: left image - center login - right image */}
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* Left campus image */}
        <div className="hidden lg:flex flex-col items-center self-start mt-10 md:mt-14">
          <img
            src="/images/tnau-campus-left.jpeg"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/images/tnau-campus-left.jpeg.jpeg'; }}
            alt="TNAU Campus"
            className="w-[360px] h-48 md:h-56 object-cover rounded-xl shadow-soft"
          />
          <img
            src="/images/tnau-campus-left.jpeg"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/images/tnau-campus-left.jpeg.jpeg'; }}
            alt="TNAU Campus Reflection"
            className="w-[360px] h-12 md:h-14 object-cover rounded-xl opacity-40 -scale-y-100 mt-3"
            style={{ WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)', maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' }}
          />
        </div>

        {/* Center login card */}
        <div className="w-full mx-auto flex flex-col items-center">
        {/* Login Form */}
          <div className="card p-8 w-full max-w-[360px]">
            {/* Logo inside card to align side image tops with card top */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-2 bg-white shadow-soft">
                <img src="/images/tnau-logo.jpeg" alt="TNAU" className="w-20 h-20 object-contain" />
              </div>
              <p className="text-gray-800 font-semibold">TNAU</p>
            </div>
            
          {!showForgotPassword ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Username Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    {...register('username', { required: 'Username is required' })}
                    className="input-field pl-10"
                    placeholder="Enter your username"
                  />
                </div>
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', { required: 'Password is required' })}
                    className="input-field pl-10 pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              {/* Location access is requested after login from the app shell */}

                {/* Sign In Button (blue) */}
              <button
                type="submit"
                disabled={isLoading}
                  className="w-full flex items-center justify-center bg-secondary-600 hover:bg-secondary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : null}
                  {isLoading ? 'Signing In...' : 'Login'}
              </button>

                {/* Forgot Password Button (green) */}
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Forgot Password
                </button>
            </form>
          ) : (
            /* Forgot Password Form - Multi-step flow */
            <div className="space-y-6">
              {/* Step 1: Email Input */}
              {resetStep === 'email' && (
                <>
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Reset Password</h2>
                    <p className="text-gray-600">Enter your email to receive a reset code</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        className="input-field pl-10"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={resetForgotPasswordFlow}
                      className="btn-outline flex-1"
                    >
                      Back to Login
                    </button>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isLoading}
                      className="btn-primary flex-1"
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Code'}
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Code Verification */}
              {resetStep === 'code' && (
                <>
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Enter Reset Code</h2>
                    <p className="text-gray-600">We've sent a 6-digit code to {forgotPasswordEmail}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reset Code
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="input-field pl-10 text-center text-lg tracking-widest"
                        placeholder="000000"
                        maxLength={6}
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setResetStep('email')}
                      className="btn-outline flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleVerifyCode}
                      disabled={isLoading || resetCode.length !== 6}
                      className="btn-primary flex-1"
                    >
                      {isLoading ? 'Verifying...' : 'Verify Code'}
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: New Password */}
              {resetStep === 'newPassword' && (
                <>
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Set New Password</h2>
                    <p className="text-gray-600">Create a new password for your account</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-field pl-10 pr-10"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="input-field pl-10 pr-10"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setResetStep('code')}
                      className="btn-outline flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                      className="btn-primary flex-1"
                    >
                      {isLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
            <p className="text-sm text-white/80">
                          © 2024 TNAU Research Trial Management. All rights reserved.
          </p>
          </div>
        </div>

        {/* Right campus image */}
        <div className="hidden lg:flex flex-col items-center self-start mt-10 md:mt-14">
          <img
            src="/images/tnau-campus-right.jpeg"
            alt="TNAU Campus"
            className="w-[360px] h-48 md:h-56 object-cover rounded-xl shadow-soft"
          />
          <img
            src="/images/tnau-campus-right.jpeg"
            alt="TNAU Campus Reflection"
            className="w-[360px] h-12 md:h-14 object-cover rounded-xl opacity-40 -scale-y-100 mt-3"
            style={{ WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)', maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' }}
          />
        </div>
      </div>
    </div>
  );
};

export default Login; 